import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { MedicationHistoryBody } from '@features/eprescriptions';
import {
  useAddDiagnosisMutation,
  useAddMedicationMutation,
  useRemoveDiagnosisMutation,
  useRemoveMedicationMutation,
} from '@features/patients/store/api';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { FeatureGates } from '@services/statsig/constants';
import { calendarDateInTimeZone, displayCalendarDate, localCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';
import { useModal } from '@store/hooks/useModal';
import { formatMedicationDetail } from '@utils/patientUtils';
import { AnimatePresence, motion } from 'motion/react';
import { useSnackbar } from 'notistack';

import { useGetServerUiComponentQuery } from '../../store';
import type {
  ClinicalChartAllergy,
  ClinicalChartData,
  ClinicalChartDiagnosis,
  ClinicalChartMedication,
} from '../../types';
import { useServerUiScope } from '../ServerUiDomainProvider';

type ClinicalChartRendererProps = {
  typeKey: string;
  data: ClinicalChartData;
};

// --- Formatters ---

const formatDiagnosisSummary = (d: ClinicalChartDiagnosis) =>
  d.code ? `${d.name} (${d.code})` : d.name;

const formatMedicationSummary = (m: ClinicalChartMedication) => {
  const detail = formatMedicationDetail(m.strength, m.dosage, m.frequency);
  return detail ? `${m.name} ${detail}` : m.name;
};

// Calendar strings retain their written date; datetimes follow the caller's display policy.
function formatClinicalMonth(
  value: string | null | undefined,
  timeZone: 'UTC' | 'local',
): string | null {
  if (!value) return null;
  const options = { month: 'short', year: 'numeric' } as const;
  const calendar = parseCalendarDate(value);
  if (calendar) return displayCalendarDate(calendar, 'en-US', options);
  if (!/^\d{4}-\d{2}-\d{2}T/.test(value) || !parseCalendarDate(value.slice(0, 10))) return null;
  try {
    const instant = new Date(value);
    const date = timeZone === 'UTC'
      ? calendarDateInTimeZone(instant, 'UTC')
      : localCalendarDate(instant);
    return displayCalendarDate(date, 'en-US', options);
  } catch {
    return null;
  }
}

// Match the diagnosis table's UTC calendar, including onset dates encoded as datetimes.
const formatDiagnosisMeta = (d: ClinicalChartDiagnosis) =>
  [d.code, d.status, formatClinicalMonth(d.date_added, 'UTC')]
    .filter(Boolean)
    .join(' · ');

// --- Summary row (collapsed view) ---

const ClinicalRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: 'text.primary', minWidth: 64, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {value}
    </Typography>
  </Box>
);

// --- Expanded section components ---

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.5, display: 'block' }}
  >
    {children}
  </Typography>
);

const DiagnosisItem: React.FC<{ diagnosis: ClinicalChartDiagnosis; onClick?: () => void }> = ({ diagnosis, onClick }) => (
  <Box
    onClick={onClick}
    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
  >
    <Typography variant="body2">{diagnosis.name}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
      {formatDiagnosisMeta(diagnosis)}
    </Typography>
  </Box>
);

const MedicationItem: React.FC<{ medication: ClinicalChartMedication; onClick?: () => void }> = ({ medication, onClick }) => {
  const detail = formatMedicationDetail(medication.strength, medication.dosage, medication.frequency);
  return (
    <Box
      onClick={onClick}
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Typography variant="body2">
        {medication.name}
        {detail && (
          <Typography component="span" variant="body2" color="text.secondary">
            {' '}{detail}
          </Typography>
        )}
      </Typography>
      {medication.date_prescribed && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
          {formatClinicalMonth(medication.date_prescribed, 'local')}
        </Typography>
      )}
    </Box>
  );
};

const AllergyItem: React.FC<{ allergy: ClinicalChartAllergy; onClick?: () => void }> = ({ allergy, onClick }) => (
  <Box
    onClick={onClick}
    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
  >
    <Typography variant="body2">{allergy.allergen}</Typography>
    {(allergy.severity || allergy.reaction) && (
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
        {[allergy.severity, allergy.reaction].filter(Boolean).join(' · ')}
      </Typography>
    )}
  </Box>
);

const AddButton: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => (
  <Button
    variant="text"
    size="small"
    color="primary"
    onClick={onClick}
    sx={{ textTransform: 'none', p: 0, minWidth: 'auto', mt: 0.5 }}
  >
    + {label}
  </Button>
);

// --- Suggestion item ---

const SuggestionItem: React.FC<{
  type: 'diagnosis' | 'medication';
  name: string;
  detail?: string | null;
  onAccept: () => void;
  onDismiss: () => void;
  isLoading?: boolean;
}> = ({ type, name, detail, onAccept, onDismiss, isLoading }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      p: 1,
      border: '1px dashed',
      borderColor: 'grey.300',
      borderRadius: 2,
      bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
    }}
  >
    <Typography variant="body2" sx={{ minWidth: 0 }}>
      <Typography component="span" variant="overline" sx={{ fontSize: '0.625rem', mr: 0.5 }}>
        {type === 'diagnosis' ? 'DIAGNOSIS' : 'MEDICATION'}
      </Typography>
      {name}
      {detail && (
        <Typography component="span" variant="body2" color="text.secondary">
          {' '}{detail}
        </Typography>
      )}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{alignItems: "center",  flexShrink: 0, ml: 1 }}>
      <Button
        variant="contained"
        size="small"
        disabled={isLoading}
        onClick={onAccept}
        sx={{ textTransform: 'none', borderRadius: 1, px: 1.5, py: 0.25, fontSize: '0.75rem' }}
      >
        Accept
      </Button>
      <Button
        variant="text"
        size="small"
        color="inherit"
        disabled={isLoading}
        onClick={onDismiss}
        sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
      >
        Dismiss
      </Button>
    </Stack>
  </Box>
);

// --- Medication history sub-section ---

/**
 * Fetches the standalone medication_history server-UI component for the
 * current patient and renders it inline beneath allergies. The component is
 * intentionally NOT registered in any section on the backend — we only
 * surface it here so the layout reads as one card, matching the prototype.
 */
// While the Celery pull task is in flight after recording consent, poll the
// resolver so the section flips to "granted" as soon as
// last_surescripts_history_fetched_at appears — without making the user
// reload the page. Polling stops automatically once consent state leaves
// "pulling".
const PULLING_POLL_INTERVAL_MS = 3000;

const MedicationHistorySection: React.FC = () => {
  const { containerKey, entityId } = useServerUiScope();
  const dosespotSyncEnabled = useFeatureGate(FeatureGates.DOSESPOT_SYNC);
  const { data: medicationHistoryComponent, isLoading, refetch } =
    useGetServerUiComponentQuery(
      {
        containerKey: containerKey ?? '',
        typeKey: 'medication_history',
        entityId: entityId ?? undefined,
      },
      { skip: !containerKey || !entityId || !dosespotSyncEnabled },
    );

  const consentState = (medicationHistoryComponent?.data as
    | { consent?: { state?: string } | null }
    | undefined)?.consent?.state;

  useEffect(() => {
    if (consentState !== 'pulling') return;
    const id = setInterval(() => {
      void refetch();
    }, PULLING_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [consentState, refetch]);

  if (!dosespotSyncEnabled) {
    return null;
  }

  if (isLoading || !medicationHistoryComponent) {
    return null;
  }

  // Hide the section entirely for patients not connected to DoseSpot — the
  // SureScripts pull isn't available, so there's no useful CTA to show.
  if (consentState === 'unavailable') {
    return null;
  }

  return (
    <Box>
      <SectionTitle>Medication history</SectionTitle>
      <MedicationHistoryBody component={medicationHistoryComponent} />
    </Box>
  );
};

// --- Main renderer ---

export const ClinicalChartRenderer: React.FC<ClinicalChartRendererProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const { open } = useModal();
  const { entityId: patientId } = useServerUiScope();
  const { clinicalChart: tracking } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // Mutation hooks for accepting/dismissing suggestions
  const [addDiagnosis] = useAddDiagnosisMutation();
  const [addMedication] = useAddMedicationMutation();
  const [removeDiagnosis] = useRemoveDiagnosisMutation();
  const [removeMedication] = useRemoveMedicationMutation();

  const diagnoses = useMemo(() => data.diagnoses ?? [], [data.diagnoses]);
  const medications = useMemo(() => data.medications ?? [], [data.medications]);
  const allergies = useMemo(() => data.allergies ?? [], [data.allergies]);

  const {
    activeDx,
    activeMeds,
    discontinuedMeds,
    suggestedDx,
    suggestedMeds,
    hasSuggestions,
    suggestionsCount,
  } = useMemo(() => {
    const aDx = diagnoses.filter(d => d.status !== 'suggested' && d.status !== 'rejected');
    const aMeds = medications.filter(m => m.status !== 'suggested' && m.status !== 'rejected' && m.status !== 'discontinued');
    const dMeds = medications.filter(m => m.status === 'discontinued');
    const sDx = diagnoses.filter(d => d.status === 'suggested');
    const sMeds = medications.filter(m => m.status === 'suggested');
    return {
      activeDx: aDx,
      activeMeds: aMeds,
      discontinuedMeds: dMeds,
      suggestedDx: sDx,
      suggestedMeds: sMeds,
      hasSuggestions: sDx.length + sMeds.length > 0,
      suggestionsCount: sDx.length + sMeds.length,
    };
  }, [diagnoses, medications]);

  const openModal = useCallback(
    (modalType: string, itemType: 'diagnosis' | 'medication' | 'allergy', editProps?: Record<string, unknown>) => {
      if (!patientId) return;
      tracking.addModalOpened({ patient_id: patientId, item_type: itemType });
      open(modalType as Parameters<typeof open>[0], { patientId, ...editProps });
    },
    [patientId, open, tracking],
  );

  // Suggestion handlers — no manual invalidateAndRefetch needed,
  // componentRefreshMiddleware handles cache refresh on mutation fulfillment.
  const handleAcceptDiagnosis = useCallback(
    async (d: ClinicalChartDiagnosis) => {
      if (!patientId) return;
      try {
        await addDiagnosis({
          patientId,
          addDiagnosisRequest: { name: d.name, code: d.code },
        }).unwrap();
        tracking.suggestionAccepted({ patient_id: patientId, item_type: 'diagnosis' });
      } catch {
        enqueueSnackbar('Failed to accept suggestion', { variant: 'error' });
      }
    },
    [patientId, addDiagnosis, tracking, enqueueSnackbar],
  );

  const handleAcceptMedication = useCallback(
    async (m: ClinicalChartMedication) => {
      if (!patientId) return;
      try {
        await addMedication({
          patientId,
          addMedicationRequest: { name: m.name, dosage: m.dosage, frequency: m.frequency },
        }).unwrap();
        tracking.suggestionAccepted({ patient_id: patientId, item_type: 'medication' });
      } catch {
        enqueueSnackbar('Failed to accept suggestion', { variant: 'error' });
      }
    },
    [patientId, addMedication, tracking, enqueueSnackbar],
  );

  const handleDismissDiagnosis = useCallback(
    async (d: ClinicalChartDiagnosis) => {
      if (!patientId) return;
      try {
        await removeDiagnosis({ patientId, diagnosisId: d.diagnosis_id }).unwrap();
        tracking.suggestionDismissed({ patient_id: patientId, item_type: 'diagnosis' });
      } catch {
        enqueueSnackbar('Failed to dismiss suggestion', { variant: 'error' });
      }
    },
    [patientId, removeDiagnosis, tracking, enqueueSnackbar],
  );

  const handleDismissMedication = useCallback(
    async (m: ClinicalChartMedication) => {
      if (!patientId) return;
      try {
        await removeMedication({ patientId, medicationId: m.medication_id }).unwrap();
        tracking.suggestionDismissed({ patient_id: patientId, item_type: 'medication' });
      } catch {
        enqueueSnackbar('Failed to dismiss suggestion', { variant: 'error' });
      }
    },
    [patientId, removeMedication, tracking, enqueueSnackbar],
  );

  const dxSummary = activeDx.map(formatDiagnosisSummary).join(' · ') || '—';
  const medsSummary = activeMeds.map(formatMedicationSummary).join(' · ') || '—';
  const allergiesSummary = allergies.length > 0
    ? allergies.map(a => a.allergen).join(' · ')
    : 'NKDA';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 2,
        p: '12px 16px',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      {/* Collapsed: clickable summary + toggle */}
      <Box
        onClick={() => {
          const next = !expanded;
          setExpanded(next);
          if (patientId) {
            (next ? tracking.expanded : tracking.collapsed)({ patient_id: patientId });
          }
        }}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, cursor: 'pointer' }}
      >
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <ClinicalRow label="Dx" value={dxSummary} />
          <ClinicalRow label="Meds" value={medsSummary} />
          <ClinicalRow label="Allergies" value={allergiesSummary} />
        </Stack>

        {/* Toggle + suggestions badge — sits to the right */}
        <Stack direction="row" spacing={0.5} sx={{alignItems: "center",  flexShrink: 0, pt: 0.25 }}>
          {hasSuggestions && !expanded && (
            <Chip
              label={`${suggestionsCount} suggested`}
              size="small"
              sx={{
                fontSize: '0.6875rem',
                height: 20,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.dark,
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '0.75rem', gap: 0.25 }}
          >
            {expanded ? 'Collapse' : 'Details'}
            {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          </Typography>
        </Stack>
      </Box>

      {/* Expanded detail */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={2}>
              {/* Diagnoses */}
              <Box>
                <SectionTitle>Diagnoses</SectionTitle>
                {activeDx.map((d, i) => (
                  <DiagnosisItem
                    key={d.diagnosis_id || i}
                    diagnosis={d}
                    onClick={() => openModal(MODAL_TYPES.ADD_DIAGNOSIS, 'diagnosis', {
                      diagnosisId: d.diagnosis_id,
                      defaultValues: { name: d.name, code: d.code ?? '', date_added: d.date_added ?? '', end_date: d.end_date ?? '', is_active: d.is_active ?? true },
                    })}
                  />
                ))}
                <AddButton label="Add diagnosis" onClick={() => openModal(MODAL_TYPES.ADD_DIAGNOSIS, 'diagnosis')} />
              </Box>

              {/* Medications */}
              <Box>
                <SectionTitle>Medications</SectionTitle>
                {activeMeds.map((m, i) => (
                  <MedicationItem
                    key={m.medication_id || i}
                    medication={m}
                    onClick={() => openModal(MODAL_TYPES.ADD_MEDICATION, 'medication', {
                      medicationId: m.medication_id,
                      defaultValues: {
                        name: m.name, generic_name: m.generic_name ?? '', strength: m.strength ?? '',
                        form: m.form ?? '', route: m.route ?? '', dosage: m.dosage ?? '',
                        frequency: m.frequency ?? '', timing: m.timing ?? '',
                        start_date: m.start_date ?? '', end_date: m.end_date ?? '',
                        indication: m.indication ?? '', is_active: m.is_active ?? true,
                      },
                    })}
                  />
                ))}
                {discontinuedMeds.length > 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    Discontinued:{' '}
                    {discontinuedMeds.map((m, i) => (
                      <Typography
                        key={m.medication_id || i}
                        component="span"
                        variant="caption"
                        color="text.disabled"
                        onClick={() => openModal(MODAL_TYPES.ADD_MEDICATION, 'medication', {
                          medicationId: m.medication_id,
                          defaultValues: {
                            name: m.name, generic_name: m.generic_name ?? '', strength: m.strength ?? '',
                            form: m.form ?? '', route: m.route ?? '', dosage: m.dosage ?? '',
                            frequency: m.frequency ?? '', timing: m.timing ?? '',
                            start_date: m.start_date ?? '', end_date: m.end_date ?? '',
                            indication: m.indication ?? '', is_active: m.is_active ?? false,
                          },
                        })}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {m.name}{i < discontinuedMeds.length - 1 ? ' · ' : ''}
                      </Typography>
                    ))}
                  </Typography>
                )}
                <AddButton label="Add medication" onClick={() => openModal(MODAL_TYPES.ADD_MEDICATION, 'medication')} />
              </Box>

              {/* Allergies */}
              <Box>
                <SectionTitle>Allergies</SectionTitle>
                {allergies.length > 0 ? (
                  allergies.map((a, i) => (
                    <AllergyItem
                      key={a.allergy_id || i}
                      allergy={a}
                      onClick={() => openModal(MODAL_TYPES.ADD_ALLERGY, 'allergy', {
                        allergyId: a.allergy_id,
                        defaultValues: { allergen: a.allergen, reaction: a.reaction ?? '', severity: a.severity, date_added: a.date_added ?? '', onset_date: a.onset_date ?? '', is_active: a.is_active ?? true },
                      })}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No known drug allergies (NKDA)
                  </Typography>
                )}
                <AddButton label="Add allergy" onClick={() => openModal(MODAL_TYPES.ADD_ALLERGY, 'allergy')} />
              </Box>

              {/* Medication history (SureScripts + self-reported, consent-gated).
                  Fetched as its own server-ui component to keep the resolver
                  and pagination logic alongside the other history primitives,
                  but rendered inline here so the prototype's "history sits
                  under allergies" layout reads as one card. */}
              <MedicationHistorySection />

              {/* Suggestions */}
              {hasSuggestions && (
                <Box sx={{ borderTop: '1px dashed', borderColor: 'grey.300', pt: 1.5 }}>
                  <Stack direction="row" spacing={0.5} sx={{alignItems: "center",  mb: 0.5 }}>
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: 0.5 }}>
                      Suggested from sessions
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Accept to add to the chart
                  </Typography>
                  <Stack spacing={1}>
                    {suggestedDx.map((d, i) => (
                      <SuggestionItem
                        key={d.diagnosis_id || `dx-${i}`}
                        type="diagnosis"
                        name={d.name}
                        detail={d.code}
                        onAccept={() => void handleAcceptDiagnosis(d)}
                        onDismiss={() => void handleDismissDiagnosis(d)}
                      />
                    ))}
                    {suggestedMeds.map((m, i) => (
                      <SuggestionItem
                        key={m.medication_id || `med-${i}`}
                        type="medication"
                        name={formatMedicationSummary(m)}
                        detail={null}
                        onAccept={() => void handleAcceptMedication(m)}
                        onDismiss={() => void handleDismissMedication(m)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
